<template>
  <BContentBox>
    <template #content>
      <div
        v-if="selectedObject.length <= 0"
      >
        <SelectObject
          @select-object="selectObject"
        />
        <div
          v-if="isAvailableProductManagementFeature"
          class="pl-600 pb-600"
        >
          <div class="pt-400 pb-100">
            <h3 class="fs-400">
              {{ $t(`sfCustomObjectProductMappings.title`) }}
            </h3>
          </div>
          <div class="header-text">
            <span>{{ $t("sfCustomObjectProductMappings.description") }}</span>
          </div>
          <div class="type-header-text pb-100">
            <span>{{ $t("sfObjectMappings.targetObjectLabel") }}</span>
          </div>
          <div
            class="select-user py-300"
            @click="selectProductMapping"
          >
            <span>{{ $t('sfCustomObjectProductMappings.selectCustomObject') }}</span>
          </div>
        </div>
        <div class="pl-600 pb-600">
          <div class="pt-400 pb-100">
            <h3 class="fs-400">
              {{ $t(`corpSettingApi.salesforce.subTabs.relationUser`) }}
            </h3>
          </div>
          <div class="header-text">
            <span>{{ $t("salesforceSettings.syncObjectType.userMappingDesc") }}</span>
          </div>
          <div class="type-header-text pb-100">
            <span>{{ $t("sfObjectMappings.targetObjectLabel") }}</span>
          </div>
          <div
            class="select-user py-300"
            @click="() => selectMapping('salesforce', 'salesforceRelationUser')"
          >
            <span>{{ $t('corpSettingApi.salesforce.subTabs.relationUser') }}</span>
          </div>
        </div>
        <div class="pl-600 pb-600">
          <div class="pt-400 pb-300">
            <h3 class="fs-400">
              {{ $t(`corpSettingApi.salesforce.subTabs.syncLead`) }}
            </h3>
          </div>
          <div class="explain">
            <span>{{ $t("salesforceSettings.syncObjectType.syncObjectTypeDesc") }}</span>
          </div>
          <BListItem
            class="radio-area mb-200"
          >
            <div class="radio-group">
              <BRadio
                v-for="syncSfObjectValue in salesforceApiInfo.syncSfObjects"
                :key="syncSfObjectValue"
                v-model="syncSfObject"
                class="radio-item"
                :label="syncSfObjectValue"
              >
                {{ $t(`salesforceSettings.syncObjectType.enum.${convertToCamelcaseKeys(syncSfObjectValue)}`) }}{{ $t('general.from') }}{{ $t('general.create.text') }}
              </BRadio>
            </div>
          </BListItem>
          <BBtn
            type="primary"
            @click="updateSyncSfObject"
          >
            <span>{{ $t('general.save.text') }}</span>
          </BBtn>
        </div>
        <TodoSync />
      </div>
      <div v-else>
        <ProductMapping
          v-if="selectedObject === 'product'"
          @back="backAction"
        />
        <MappingColumn
          v-else
          :selected-object="selectedObject"
          @back="backAction"
        />
      </div>
    </template>
  </BContentBox>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { SfSalesforceSettingsService } from '@/api/user/resources/salesforce/salesforce_settings';
import { SfObjectMappingService } from '@/api/user/resources/salesforce/sf_object_mapping';
import MappingColumn from '@/components/organisms/user/setting/api/SalesforceApiMap/MappingColumn.vue';
import ProductMapping from '@/components/organisms/user/setting/api/SalesforceApiMap/ProductMapping.vue';
import SelectObject from '@/components/organisms/user/setting/api/SalesforceApiMap/SelectObject.vue';
import TodoSync from '@/components/organisms/user/setting/api/TodoSync.vue';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useToast } from '@/composable/toast';

type TProps = {
  salesforceApiInfo: {
    syncSfObject: string,
    syncSfObjects: string[],
  },
};
const props = defineProps<TProps>();

const emit = defineEmits(['change-subtabs']);
const { successToast } = useToast();
const { isAvailableProductManagementFeature } = useAvailableFeatureCheck();
const i18n = useI18n();
const apiService = new SfObjectMappingService();
const apiSalesforceSettingsService = new SfSalesforceSettingsService();
const salesforceSettingLoading = ref(true);
const sfObjectMappingsKeys = ref([]);
const selectedObject = ref('');
const syncSfObject = ref<string>('lead');

const selectMapping = (tabType: string, pageName: string) => {
  emit('change-subtabs', tabType, pageName);
};

const selectObject = (object: string) => {
  selectedObject.value = object;
};

const selectProductMapping = () => {
  selectedObject.value = 'product';
};

const backAction = () => {
  selectedObject.value = '';
};

const updateSyncSfObject = async () => {
  await apiSalesforceSettingsService.updateSyncSfObject({ syncSfObject: syncSfObject.value });
  successToast(i18n.t('general.success.to', { action: i18n.t('general.update.text') }));
};

const convertToCamelcaseKeys = (dataType: string) => {
  if (dataType.indexOf('_') === -1) {
    return dataType;
  }
  return dataType.split('_').map((word, index) => {
    if (index === 0) {
      return word.toLowerCase();
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join('');
};

if (props.salesforceApiInfo.syncSfObject.length) {
  syncSfObject.value = props.salesforceApiInfo.syncSfObject;
}

onMounted(async () => {
  const result = await apiService.getSfObjectMappingsKeys();
  sfObjectMappingsKeys.value = result.data.sfObjectMappingsKeys;
  salesforceSettingLoading.value = false;
});
</script>

<style lang="scss" scoped>
.header-text {
  color: $textcolor-light;
  margin-bottom: 24px;
  white-space: pre-wrap;
}

.type-header-text {
  color: $textcolor-light;
  white-space: pre-wrap;
}

.select-user {
  cursor: pointer;

  &:hover {
    background-color: $bgcolor-base;
  }
  &:first-child {
    margin-top: -8px;
  }
  &:not(:last-child) {
    border-bottom: 1px solid $bdcolor-base;
  }
  span {
    font-weight: bold;
  }
}

.radio-group {
  display: flex;
  flex-direction: column;
}

.explain {
  color: $concrete-dark;
  margin-bottom: 20px;
  white-space: pre-wrap;
}
</style>
